import React, { useEffect, useState, useCallback } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import PrimaryButton from "../buttons/PrimaryButton";
import { parseISO, format } from "date-fns";
import axios from "axios";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import Pagination from "../pagination/Pagination";
import Cookies from "js-cookie";
import '../../styles/buttons/buttons.css';

const ScannerOperatorsTable = ({
  searchQuery,
  refreshTrigger,
  onAssignClick,
}) => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [error, setError] = useState("");
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [isAllSelected, setIsAllSelected] = useState(false);

  const baseUrl = process.env.REACT_APP_BASE_URL;
  const entriesPerPage = 10;

  const indexOfLastEntry = currentPage * entriesPerPage;
  const indexOfFirstEntry = indexOfLastEntry - entriesPerPage;
  const currentEntries = filteredUsers.slice(
    indexOfFirstEntry,
    indexOfLastEntry
  );

  const totalPages = Math.ceil(filteredUsers.length / entriesPerPage);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const parsedDate = parseISO(dateString);
    return format(parsedDate, "dd MMM yyyy");
  };

  // Disable delete for inactive users
  const deleteDisabled = (status) => {
    return status === "Not Active";
  };

  // Handle select all checkbox
  const handleSelectAll = (e) => {
    const isChecked = e.target.checked;
    setIsAllSelected(isChecked);

    if (isChecked) {
      setSelectedUsers(
        currentEntries
          .filter((user) => !deleteDisabled(user.status))
          .map((user) => user.uuid)
      );
    } else {
      setSelectedUsers([]);
    }
  };

  // Handle individual user selection
  const handleSelectUser = (e, userId) => {
    const isChecked = e.target.checked;

    setSelectedUsers((prev) => {
      if (isChecked) {
        return [...prev, userId];
      } else {
        const newSelected = prev.filter((id) => id !== userId);
        setIsAllSelected(
          newSelected.length ===
            currentEntries.filter((user) => !deleteDisabled(user.status)).length
        );
        return newSelected;
      }
    });
  };

  // Reset selections when filteredUsers change
  useEffect(() => {
    setSelectedUsers([]);
    setIsAllSelected(false);
  }, [filteredUsers]);

  // Fetch scanner operators
  useEffect(() => {
    const getScannerOperators = async () => {
      try {
        const token = Cookies.get("token");
        const res = await axios.get(
          `${baseUrl}/api/users/scanner-operators/details`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        const allAssignments = res.data
          .flatMap((operator) =>
            operator.assignedSites.length > 0
              ? operator.assignedSites.map((site) => ({
                  operatorId: operator.id,
                  operatorName: operator.name,
                  email: operator.email,
                  siteId: site.siteId,
                  province: site.province,
                  district: site.district,
                  sector: site.sector,
                  assignedAt: site.assignedAt,
                }))
              : [
                  {
                    operatorId: operator.id,
                    operatorName: operator.name,
                    email: operator.email,
                    siteId: null,
                    province: null,
                    district: null,
                    sector: null,
                    assignedAt: null,
                  },
                ]
          )
          .sort((a, b) => {
            if (!a.assignedAt && !b.assignedAt)
              return b.operatorId - a.operatorId;
            if (!a.assignedAt) return 1;
            if (!b.assignedAt) return -1;
            return new Date(b.assignedAt) - new Date(a.assignedAt);
          });

        setUsers(allAssignments);
        setUsers(allAssignments);
      } catch (error) {
        console.error("Error fetching scanner operators:", error);
        setError("Error fetching scanner operators");
      } finally {
        setLoading(false);
      }
    };
    getScannerOperators();
  }, [baseUrl, refreshTrigger]);

  // Handle search filtering
  useEffect(() => {
    const handleFilters = async () => {
      setSearchLoading(true);
      let filtered = [...users];

      if (searchQuery?.trim()) {
        const searchLower = searchQuery.toLowerCase().trim();
        filtered = filtered.filter((user) => {
          const nameMatch = user.operatorName
            ?.toLowerCase()
            .includes(searchLower);
          const emailMatch = user.email?.toLowerCase().includes(searchLower);
          const provinceMatch = user.province
            ?.toLowerCase()
            .includes(searchLower);
          const districtMatch = user.district
            ?.toLowerCase()
            .includes(searchLower);
          const sectorMatch = user.sector?.toLowerCase().includes(searchLower);
          return (
            nameMatch ||
            emailMatch ||
            provinceMatch ||
            districtMatch ||
            sectorMatch
          );
        });
      }

      setFilteredUsers(filtered);
      setSearchLoading(false);
    };

    handleFilters();
  }, [searchQuery, users]);

  return (
    <>
      <table className="admin">
        <thead>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Province</th>
            <th>District</th>
            <th>Sector</th>
            <th>Assigned At</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {loading || searchLoading ? (
            Array.from({ length: 5 }).map((_, index) => (
              <tr key={index}>
                <td>
                  <Skeleton width={20} height={20} />
                </td>
                <td>
                  <Skeleton width={100} />
                </td>
                <td>
                  <Skeleton width={150} />
                </td>
                <td>
                  <Skeleton width={100} />
                </td>
                <td>
                  <Skeleton width={100} />
                </td>
                <td>
                  <Skeleton width={100} />
                </td>
                <td>
                  <Skeleton width={80} />
                </td>
                <td>
                  <Skeleton width={100} />
                </td>
              </tr>
            ))
          ) : filteredUsers.length === 0 ? (
            <tr style={{ boxShadow: "none" }}>
              <td colSpan="8" style={{ textAlign: "center", color: "#9B9B9B" }}>
                No scanner operators found
              </td>
            </tr>
          ) : (
            currentEntries.map((user, index) => (
              <tr key={index + 1}>
                <td>{user.operatorName}</td>
                <td>{user.email}</td>
                <td>{user.province || "-"}</td>
                <td>{user.district || "-"}</td>
                <td>{user.sector || "-"}</td>
                <td>{formatDate(user.assignedAt)}</td>
                <td>
                 
                    <PrimaryButton
                        className="edit-scanner-button"
                        text="Edit"
                        onClick={() => onAssignClick({
                          operatorId: user.operatorId,
                          operatorName: user.operatorName,
                          email: user.email,
                          siteId: user.siteId,
                          province: user.province,
                          district: user.district,
                          sector: user.sector,
                        })}
                      />

                  
                     
                      
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      {!loading && filteredUsers.length > 0 && (
        <Pagination
          currentPage={currentPage}
          totalItems={filteredUsers.length}
          entriesPerPage={entriesPerPage}
          onPageChange={setCurrentPage}
        />
      )}
      {error && <div className="error-message">{error}</div>}
    </>
  );
};

export default ScannerOperatorsTable;




// import React, { useEffect, useState } from "react";
// import { Icon } from "@iconify/react/dist/iconify.js";
// import PrimaryButton from "../buttons/PrimaryButton";
// import { parseISO, format } from "date-fns";
// import axios from "axios";
// import Skeleton from "react-loading-skeleton";
// import "react-loading-skeleton/dist/skeleton.css";
// import Pagination from "../pagination/Pagination";
// import Cookies from "js-cookie";
// import PageSizeSelector from "../pagination/PageSizeSelector";
// import usePaginatedData from "../pagination/hooks/usePaginatedData";
// import '../../styles/buttons/buttons.css';

// const ScannerOperatorsTable = ({
//   searchQuery,
//   refreshTrigger,
//   onAssignClick,
// }) => {
//   // Configuration for the scanner operators endpoint
//   const endpoints = {
//     'scanner-operators': `/api/users/scanner-operators/details`
//   };

//   // Custom transform function for scanner operators data
//   const transformFunctions = {
//     'scanner-operators': (data) => {
//       const allAssignments = data
//         .flatMap((operator) =>
//           operator.assignedSites.length > 0
//             ? operator.assignedSites.map((site) => ({
//                 operatorId: operator.id,
//                 operatorName: operator.name,
//                 email: operator.email,
//                 siteId: site.siteId,
//                 province: site.province,
//                 district: site.district,
//                 sector: site.sector,
//                 assignedAt: site.assignedAt,
//               }))
//             : [
//                 {
//                   operatorId: operator.id,
//                   operatorName: operator.name,
//                   email: operator.email,
//                   siteId: null,
//                   province: null,
//                   district: null,
//                   sector: null,
//                   assignedAt: null,
//                 },
//               ]
//         )
//         // Sort by assignedAt - most recent first, then by operatorId for unassigned
//         .sort((a, b) => {
//           if (!a.assignedAt && !b.assignedAt)
//             return b.operatorId - a.operatorId;
//           if (!a.assignedAt) return 1;
//           if (!b.assignedAt) return -1;
//           return new Date(b.assignedAt) - new Date(a.assignedAt);
//         });

//       return allAssignments;
//     }
//   };

//   // Use the pagination hook - fixed activeTab to 'scanner-operators'
//   const {
//     data: filteredUsers,
//     pagination,
//     loading,
//     error: fetchError,
//     goToPage,
//     changePageSize,
//     refresh
//   } = usePaginatedData({
//     activeTab: 'scanner-operators',
//     searchQuery,
//     startDate: null, // Not needed for this table
//     endDate: null,   // Not needed for this table
//     endpoints,
//     transformFunctions
//   });

//   const [error, setError] = useState("");

//   // Format date for display
//   const formatDate = (dateString) => {
//     if (!dateString) return "-";
//     const parsedDate = parseISO(dateString);
//     return format(parsedDate, "dd MMM yyyy");
//   };

//   // Refresh data when refreshTrigger changes
//   useEffect(() => {
//     if (refreshTrigger) {
//       refresh();
//     }
//   }, [refreshTrigger, refresh]);

//   // Handle page change
//   const handlePageChange = (newPage) => {
//     goToPage(newPage);
//   };

//   // Handle page size change
//   const handlePageSizeChange = (event) => {
//     const newPageSize = parseInt(event.target.value);
//     changePageSize(newPageSize);
//   };

//   return (
//     <>
//       {/* Page Size Selector */}
//       <div className="table-header-controls">
//         <PageSizeSelector
//           value={pagination.itemsPerPage}
//           onChange={handlePageSizeChange}
//           disabled={loading}
//         />
        
//         {/* Show error message if fetch failed */}
//         {fetchError && (
//           <div className="error-message">
//             <span style={{ color: "#E20000", marginRight: "10px" }}>
//               Error: {fetchError}
//             </span>
//             <button 
//               onClick={refresh} 
//               className="retry-button"
//               disabled={loading}
//             >
//               <Icon icon="iconamoon:restart" width="16" height="16" />
//               Retry
//             </button>
//           </div>
//         )}
//       </div>

//       <table className="admin">
//         <thead>
//           <tr>
//             <th>Name</th>
//             <th>Email</th>
//             <th>Province</th>
//             <th>District</th>
//             <th>Sector</th>
//             <th>Assigned At</th>
//             <th>Action</th>
//           </tr>
//         </thead>
//         <tbody>
//           {loading ? (
//             Array.from({ length: 5 }).map((_, index) => (
//               <tr key={index}>
//                 <td>
//                   <Skeleton width={100} />
//                 </td>
//                 <td>
//                   <Skeleton width={150} />
//                 </td>
//                 <td>
//                   <Skeleton width={100} />
//                 </td>
//                 <td>
//                   <Skeleton width={100} />
//                 </td>
//                 <td>
//                   <Skeleton width={100} />
//                 </td>
//                 <td>
//                   <Skeleton width={100} />
//                 </td>
//                 <td>
//                   <Skeleton width={80} />
//                 </td>
//               </tr>
//             ))
//           ) : filteredUsers.length === 0 ? (
//             <tr style={{ boxShadow: "none" }}>
//               <td colSpan="7" style={{ textAlign: "center", color: "#9B9B9B" }}>
//                 No scanner operators found
//               </td>
//             </tr>
//           ) : (
//             filteredUsers.map((user, index) => (
//               <tr key={`${user.operatorId}-${user.siteId || 'unassigned'}-${index}`}>
//                 <td>{user.operatorName}</td>
//                 <td>{user.email}</td>
//                 <td>{user.province || "-"}</td>
//                 <td>{user.district || "-"}</td>
//                 <td>{user.sector || "-"}</td>
//                 <td>{formatDate(user.assignedAt)}</td>
//                 <td>
//                   <PrimaryButton
//                     className="edit-scanner-button"
//                     text="Edit"
//                     onClick={() => onAssignClick({
//                       operatorId: user.operatorId,
//                       operatorName: user.operatorName,
//                       email: user.email,
//                       siteId: user.siteId,
//                       province: user.province,
//                       district: user.district,
//                       sector: user.sector,
//                     })}
//                   />
//                 </td>
//               </tr>
//             ))
//           )}
//         </tbody>
//       </table>

//       {/* Pagination */}
//       {!loading && pagination.totalPages > 1 && (
//         <Pagination
//           currentPage={pagination.currentPage}
//           totalItems={pagination.totalItems}
//           entriesPerPage={pagination.itemsPerPage}
//           onPageChange={handlePageChange}
//         />
//       )}

//       {/* Error display */}
//       {error && <div className="error-message">{error}</div>}
//     </>
//   );
// };

// export default ScannerOperatorsTable;
